// src/utils/editionFnct.ts — SCE Issues
// Modifier, clore, rouvrir une FNC-T (chantier FNC-T, 07/10 — remarque 4 de
// docs/chantier-fnct.md : « indispensable avant la bascule »).
//
// ─── Modifier ───
//
// Un formulaire (EditionFnct), une écriture des seules colonnes changées, UN
// événement d'historique (une ligne par champ, avant → après). Relu avant
// écriture : refusé si un champ modifié ici a bougé entre-temps (même règle
// que la modification d'un problème, Lot 6). Le risque (S, F, D, analyse AQ)
// n'est modifiable que par ceux qui le voient (utils/droitsFnct).
//
// ─── Clore ───
//
// Les règles de clôture ne sont pas encore décidées (CA / PA exigées ? risque
// obligatoire ?). Règle MINIMALE, calée sur la pratique de la base (256 FNC-T
// closes au 07/10) : toutes les actions closes (1 seule exception dans la
// base) et la légitimité renseignée (4 exceptions). Le risque non évalué (12
// closes sans) et l'absence d'action CA / PA (253 sur 256 !) sont signalés,
// pas bloquants. Une date de correction close manquante prend, par défaut, la
// dernière clôture d'une action CO.
//
// Pur : aucun réseau, aucun React.

import type { ActionFnct, Fnct, Jour } from '@/types/fnct.types'
import { CATEGORIES_FNCT, LIEUX_FNCT, PRIORITES_FNCT } from '@/config/fnct.config'
import { decouperNumerosSerie, jourLisible } from './fnct'

/** Échelles S, F, D de la base (2, 5, 7, 10 : seules valeurs utilisées). */
export const ECHELLE_RPN: readonly number[] = [2, 5, 7, 10]

/** « (AQ Opérations) » : valeurs de la base → libellé. */
export const ANALYSES_RISQUE: readonly { valeur: string; libelle: string }[] = [
  { valeur: 'Not required', libelle: 'Pas nécessaire' },
  { valeur: 'Required-New', libelle: 'Nécessaire — nouvelle' },
  { valeur: 'Required-Ongoing', libelle: 'Nécessaire — en cours' },
]

/** NC Legitimacy : valeurs de la base → libellé. */
export const LEGITIMITES: readonly { valeur: string; libelle: string }[] = [
  { valeur: 'Accepted', libelle: 'Acceptée' },
  { valeur: 'Not accepted', libelle: 'Non acceptée' },
  { valeur: 'Goodwill', libelle: 'Geste commercial' },
]

export const TYPES_PRODUIT: readonly { valeur: string; libelle: string }[] = [
  { valeur: 'Modular', libelle: 'Modulaire' },
  { valeur: 'Customized', libelle: 'Custom' },
]

/** Ce que le formulaire modifie. Les N° de série en texte libre (« 4023-H 4024-H »). */
export interface SaisieEditionFnct {
  categorie: string
  description: string
  documentsImpactes: string
  explicationPriorite: string
  numerosSerie: string
  modele: string
  typeProduit: string
  cve: string
  lieuDetection: string
  dateDetection: Jour | null
  priorite: string
  leader: string
  leaderUPN: string
  expert: string
  severite: number | null
  frequence: number | null
  detectabilite: number | null
  analyseRisque: string
}

/** Les champs du risque : ceux qui ne le voient pas ne les écrivent jamais. */
export const CHAMPS_RISQUE: readonly (keyof SaisieEditionFnct)[] = ['severite', 'frequence', 'detectabilite', 'analyseRisque']

export function saisieDepuisFnct(f: Fnct): SaisieEditionFnct {
  return {
    categorie: f.categorie,
    description: f.description,
    documentsImpactes: f.documentsImpactes,
    explicationPriorite: f.explicationPriorite,
    numerosSerie: f.numerosSerie.join(' '),
    modele: f.modele,
    typeProduit: f.typeProduit,
    cve: f.cve,
    lieuDetection: f.lieuDetection,
    dateDetection: f.dateDetection,
    priorite: f.priorite,
    leader: f.leader,
    leaderUPN: f.leaderUPN ?? '',
    expert: f.expert,
    severite: f.severite,
    frequence: f.frequence,
    detectabilite: f.detectabilite,
    analyseRisque: f.analyseRisque,
  }
}

/** La FNC-T telle qu'elle sera après la saisie (textes nettoyés, N° de série découpés). */
export function fnctApres(f: Fnct, s: SaisieEditionFnct, avecRisque: boolean): Fnct {
  const apres: Fnct = {
    ...f,
    categorie: s.categorie.trim(),
    description: s.description.trim(),
    documentsImpactes: s.documentsImpactes.trim(),
    explicationPriorite: s.explicationPriorite.trim(),
    numerosSerie: decouperNumerosSerie(s.numerosSerie),
    modele: s.modele.trim(),
    typeProduit: s.typeProduit,
    cve: s.cve.trim().toUpperCase(),
    lieuDetection: s.lieuDetection,
    dateDetection: s.dateDetection,
    priorite: s.priorite,
    leader: s.leader.trim(),
    leaderUPN: s.leaderUPN,
    expert: s.expert.trim(),
  }
  if (avecRisque) {
    apres.severite = s.severite
    apres.frequence = s.frequence
    apres.detectabilite = s.detectabilite
    apres.analyseRisque = s.analyseRisque
  }
  return apres
}

/** Ce qui manque pour enregistrer ; vide = prêt. */
export function manquantsEdition(s: SaisieEditionFnct): string[] {
  const m: string[] = []
  if (!s.categorie.trim()) m.push('la catégorie')
  if (!s.description.trim()) m.push('la description')
  if (!s.leader.trim()) m.push('le leader')
  return m
}

// ─── Ce qui change, pour l'historique ───

/** Les champs comparés, avec leur libellé et leur forme lisible. */
const CHAMPS: readonly { cle: keyof Fnct; libelle: string; lisible?: (v: unknown) => string }[] = [
  { cle: 'categorie', libelle: 'Catégorie' },
  { cle: 'description', libelle: 'Description' },
  { cle: 'documentsImpactes', libelle: 'Documents / tests impactés' },
  { cle: 'explicationPriorite', libelle: 'Justification de la priorité' },
  { cle: 'numerosSerie', libelle: 'N° de série', lisible: (v) => (Array.isArray(v) ? v.join(', ') : '') },
  { cle: 'modele', libelle: 'Modèle' },
  { cle: 'typeProduit', libelle: 'Type de produit' },
  { cle: 'cve', libelle: 'CVE' },
  { cle: 'lieuDetection', libelle: 'Lieu de détection' },
  { cle: 'dateDetection', libelle: 'Détectée le', lisible: (v) => (v ? jourLisible(String(v)) : '') },
  { cle: 'priorite', libelle: 'Priorité' },
  { cle: 'leader', libelle: 'Leader' },
  { cle: 'expert', libelle: 'Expert' },
  { cle: 'severite', libelle: 'Sévérité' },
  { cle: 'frequence', libelle: 'Fréquence' },
  { cle: 'detectabilite', libelle: 'Détectabilité' },
  { cle: 'analyseRisque', libelle: 'Analyse de risque' },
]

export interface ChangementFnct {
  cle: keyof Fnct
  libelle: string
  avant: string
  apres: string
}

const versTexte = (v: unknown): string =>
  v === null || v === undefined ? '' : Array.isArray(v) ? v.join(';') : String(v).trim()

export function changementsFnct(avant: Fnct, apres: Fnct): ChangementFnct[] {
  const res: ChangementFnct[] = []
  for (const c of CHAMPS) {
    if (versTexte(avant[c.cle]) === versTexte(apres[c.cle])) continue
    const lire = c.lisible ?? versTexte
    res.push({ cle: c.cle, libelle: c.libelle, avant: lire(avant[c.cle]), apres: lire(apres[c.cle]) })
  }
  return res
}

/**
 * Relu avant écriture : un champ que l'on modifie ET qui a bougé entre
 * l'ouverture du formulaire et maintenant → refus (on écraserait l'autre).
 * @returns les libellés en conflit ; vide = on peut écrire.
 */
export function conflitsEdition(ouverte: Fnct, relue: Fnct, changements: readonly ChangementFnct[]): string[] {
  return changements.filter((c) => versTexte(ouverte[c.cle]) !== versTexte(relue[c.cle])).map((c) => c.libelle)
}

/** Seul le risque bouge : l'événement s'appelle « Risque évalué ». */
export function seulementRisque(changements: readonly ChangementFnct[]): boolean {
  const risque = new Set<string>(CHAMPS_RISQUE)
  return changements.length > 0 && changements.every((c) => risque.has(c.cle))
}

// ─── Le risque seul (04/11) ───

/** S, F, D et l'analyse AQ : évalués depuis la section « Le risque » ou à la clôture. */
export type SaisieRisque = Pick<SaisieEditionFnct, 'severite' | 'frequence' | 'detectabilite' | 'analyseRisque'>

export function risqueDepuisFnct(f: Fnct): SaisieRisque {
  return { severite: f.severite, frequence: f.frequence, detectabilite: f.detectabilite, analyseRisque: f.analyseRisque }
}

/** Le risque n'est pas évalué tant que S ou F manque (même règle que l'avertissement de clôture). */
export function risqueAEvaluer(f: Fnct): boolean {
  return f.severite === null || f.frequence === null
}

/**
 * Ce qui change dans le risque, et seulement lui : la description ou le leader
 * vides d'une FNC-T importée n'empêchent pas de l'évaluer (contrairement à
 * « Modifier », qui les exige).
 */
export function changementsRisque(f: Fnct, r: SaisieRisque): ChangementFnct[] {
  return changementsFnct(f, { ...f, ...r, analyseRisque: r.analyseRisque.trim() })
}

/** Texte de l'événement : une ligne par champ ; les longs textes, l'ancien cité. */
export function texteModificationFnct(changements: readonly ChangementFnct[]): string {
  return changements
    .map((c) =>
      c.avant.length > 60 || c.apres.length > 60 || c.avant.includes('\n') || c.apres.includes('\n')
        ? `${c.libelle} modifiée. Avant : « ${c.avant || '(vide)'} »`
        : `${c.libelle} : ${c.avant || '(vide)'} → ${c.apres || '(vide)'}`,
    )
    .join('\n')
}

// ─── Clore ───

export interface SaisieCloture {
  cloture: Jour | null
  clotureCorrection: Jour | null
  legitimite: string
  numeroCapa: string
  /** Ajouté aux commentaires de la FNC-T (anglais : il part dans le rapport). */
  commentaire: string
}

/** Pré-rempli : aujourd'hui, la correction close à la dernière clôture d'une CO. */
export function saisieCloture(f: Fnct, actions: readonly ActionFnct[], aujourdhui: Jour): SaisieCloture {
  return {
    cloture: aujourdhui,
    clotureCorrection: f.clotureCorrection ?? derniereClotureCo(actions),
    legitimite: f.legitimite,
    numeroCapa: f.numeroCapa,
    commentaire: '',
  }
}

function derniereClotureCo(actions: readonly ActionFnct[]): Jour | null {
  const dates = actions.filter((a) => a.categorie === 'CO' && a.cloture).map((a) => a.cloture as Jour)
  return dates.length ? dates.sort().at(-1) ?? null : null
}

/** Ce qui empêche de clore ; vide = on peut. */
export function bloquantsCloture(f: Fnct, actions: readonly ActionFnct[], s: SaisieCloture, aujourdhui: Jour): string[] {
  const b: string[] = []
  if (f.cloture) b.push('elle est déjà close')
  const ouvertes = actions.filter((a) => !a.cloture)
  if (ouvertes.length > 0) b.push(`${ouvertes.length} action${ouvertes.length > 1 ? 's' : ''} encore ouverte${ouvertes.length > 1 ? 's' : ''}`)
  if (!s.legitimite) b.push('la légitimité')
  if (!s.cloture) b.push('la date de clôture')
  else if (s.cloture > aujourdhui) b.push('une date de clôture qui n’est pas dans le futur')
  else if (f.enregistrement && s.cloture < f.enregistrement) b.push('une date de clôture après l’enregistrement')
  if (s.clotureCorrection && s.cloture && s.clotureCorrection > s.cloture) b.push('une correction close avant la FNC-T')
  return b
}

/** Signalé, pas bloquant (règles de clôture pas encore décidées). */
export function avertissementsCloture(f: Fnct, actions: readonly ActionFnct[]): string[] {
  const a: string[] = []
  if (f.severite === null || f.frequence === null) a.push('Risque non évalué (S × F × D).')
  if (!actions.some((x) => x.categorie === 'CA' || x.categorie === 'PA')) a.push('Aucune action corrective ou préventive (CA / PA).')
  return a
}

/** Ajoute une ligne aux commentaires (une ligne par ajout, comme les actions). */
export function ajouterCommentaireFnct(existant: string, ligne: string): string {
  const l = ligne.trim()
  if (!l) return existant
  return existant.trim() ? `${existant.trim()}\n${l}` : l
}

export function texteClotureFnct(f: Fnct, s: SaisieCloture): string {
  return [
    `Close le ${jourLisible(s.cloture)}`,
    s.legitimite ? `Légitimité : ${libelleDe(LEGITIMITES, s.legitimite)}` : '',
    s.clotureCorrection && s.clotureCorrection !== f.clotureCorrection ? `Correction close le ${jourLisible(s.clotureCorrection)}` : '',
    s.numeroCapa.trim() && s.numeroCapa.trim() !== f.numeroCapa ? `CAPA : ${s.numeroCapa.trim()}` : '',
    s.commentaire.trim(),
  ]
    .filter(Boolean)
    .join('\n')
}

/** Rouvrir : le motif est obligatoire, la date de clôture effacée, le reste gardé. */
export function erreurReouverture(f: Pick<Fnct, 'cloture'>, motif: string): string | null {
  if (!f.cloture) return 'Elle n’est pas close.'
  if (!motif.trim()) return 'Le motif est obligatoire.'
  return null
}

// ─── Libellés ───

export function libelleDe(liste: readonly { valeur: string; libelle: string }[], valeur: string): string {
  return liste.find((x) => x.valeur === valeur)?.libelle ?? valeur
}

/** Valeurs connues des listes du formulaire (une valeur hors liste de la base reste proposée). */
export const VALEURS_CONNUES = {
  categories: CATEGORIES_FNCT.flatMap((g) => g.categories.map((c) => c.valeur)),
  lieux: LIEUX_FNCT.map((l) => l.valeur),
  priorites: PRIORITES_FNCT.map((p) => p.valeur),
}
